<template>
  <div class="matter-article"
    :data-orientation="item.featuredImage.aspect"
    :class="{'tapped': tapped}"
  >
    <router-link
      :to="{ path: item.url, params: { url: item.url, data: item.data } }"
      v-touch="touchHandler"
    >
      <div
        class="matter-article__image"
        :data-orientation="item.featuredImage.aspect"
        :style="backgroundImageStyles"
        ref="articleImage"
      />
      <div
        class="matter-article__info"
        :data-orientation="item.featuredImage.aspect"
        :style="infoStyles"
      >
        <div
          class="matter-article__info-content"
          ref="infoContent"
          :style="infoContentStyles"
        >
          <span class="matter-article__category">
            {{item.category}}
          </span>
          <span class="matter-article__title">
            {{item.title}}
          </span>
        </div>
      </div>
    </router-link>
  </div>
</template>

<script>
// Libs
import Vue from 'vue';

// Components
import { Bus } from '@/helpers/bus';

// Constants
import { ArticleTile, Events } from '@/app-constants';

// Utils
import { getResponsiveUrl } from '@/utils/image-utils';

export default {

  props: ['item'],

  data() {
    return {
      tapped: false,
      contentHeight: 0,
      imageContentHeight: 0,
      imageContentWidth: 0,
      handleTapFunc: null,
    };
  },

  computed: {

    backgroundImageStyles() {
      const { imageContentHeight, imageContentWidth } = this;
      const src = this.item.featuredImage.imageSrc;

      const maxDim = Math.max(imageContentWidth, imageContentHeight);
      const rUrl = getResponsiveUrl(src, maxDim);
      const styles = {
        backgroundImage: `url(${rUrl})`,
      };
      return styles;
    },

    isDesktop() {
      return this.$store.getters.isDesktop;
    },

    currentWidth() {
      return this.$store.state.width;
    },

    infoStyles() {
      const bottom = Math.abs(this.contentBottom);
      const margin = ArticleTile.articleMargin;
      const styles = {};
      if (this.isDesktop) {
        styles.marginBottom = `${bottom + margin}px`;
      }
      return styles;
    },

    infoContentStyles() {
      const styles = {};
      if (this.isDesktop) {
        styles.bottom = `${this.contentBottom}px`;
      }
      return styles;
    },

    contentBottom() {
      const overlap = ArticleTile.articleInfoOverlap;
      return (overlap - this.contentHeight);
    },

  },

  methods: {

    touchHandler(e) {
      if (this.tapped === false) {
        e.preventDefault();
        this.tapped = !this.tapped;
        Bus.$emit(Events.TappedArticle, this.item.slug);
      }
    },

    measureLayout() {
      this.contentHeight = this.getInfoContentHeight();
      this.imageContentHeight = this.getImageContentHeight();
      this.imageContentWidth = this.getImageContentWidth();
    },

    getInfoContentHeight() {
      const el = this.$refs.infoContent;
      return (el) ? el.clientHeight : 0;
    },

    getImageContentHeight() {
      const el = this.$refs.articleImage;
      return (el) ? el.clientHeight : 0;
    },

    getImageContentWidth() {
      const el = this.$refs.articleImage;
      return (el) ? el.clientWidth : 0;
    },

  },

  watch: {

    currentWidth(/* newVal, oldVal */) {
      Vue.nextTick(() => {
        this.measureLayout();
      });
    },

  },

  mounted() {
    this.measureLayout();
    this.handleBusTapEventFunc = (slug) => {
      // console.log(slug, this.item.slug);
      if (this.item.slug !== slug) {
        this.tapped = false;
      }
    };
    Bus.$on(Events.TappedArticle, this.handleBusTapEventFunc);
  },

  beforeDestroy() {
    Bus.$off(Events.TappedArticle, this.handleBusTapEventFunc);
  },

};
</script>

<style lang="scss">
@import '~@/styles/_typography.scss';
@import '~@/styles/_variables.scss';
@import '~@/styles/_mixins.scss';

.matter-article {
  align-self: flex-start;
  margin-bottom: 0;

  @include ngMq($nbp--tablet, up) {
    margin-bottom: 2rem;
  }
  @include ngMq($nbp--desktop, up) {
    margin-bottom: 2rem; // DYNAMIC
  }

  > a {
    display: block;
    color: $color--primary-dark;
  }

  &__image {
    position: absolute;
    background-size: cover;
    background-position: center center;
    transition: all 250ms;
    width: 100%;

    // media query props
    padding-bottom: $dimension-1-1;

    &[data-orientation="portrait"] {
      padding-bottom: $dimension-9-16;
    }

    &[data-orientation="landscape"] {
      padding-bottom: $dimension-16-9;
    }

  }

  &__info {
    transition: all 250ms;
  }

  &__info-content {
    background: white;
    padding: rem(29px);
    transition: all 250ms;
  }

  &__category {
    display: block;
    @include sansserif($size: (13, 13, 5), $weight: 'regular');
    text-transform: uppercase;
    color: $color--grey;
    transition: all 250ms;
    padding-bottom: rem(6px);
  }

  &__title {
    display: block;
    @include sansserif($size: (18, 30, 0), $weight: 'regular');
    color: black;
    transition: all 250ms;
  }
}

/*
  these are split out to make it easier to override between breakpoints
*/

@include ngMq($nbp--desktop, down) {
  .matter-article {

    &__info {
      width: 100%;
      background: white;
      opacity: 0;

      display: flex;
      align-items: center;
      justify-content: center;

      // media query props
      padding-bottom: $dimension-1-1;

      &[data-orientation="portrait"] {
        padding-bottom: $dimension-9-16;
      }
      &[data-orientation="landscape"] {
        padding-bottom: $dimension-16-9;
      }

      &-content {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    &__info-content {
      padding: 2.5rem;
    }

    &__category {
      transform: translateY(-100%);
    }

    &__title {
      transform: translateY(50%);
    }

    &:hover,
    &:active,
    &:focus,
    &.tapped {
      .matter-article__info {
        opacity: $tile--default-opacity;
      }

      .matter-article__category {
        transform: translateY(0);
      }

      .matter-article__title {
        transform: translateY(0);
      }
    }
  }
}

@include ngMq($nbp--desktop, up) {
  .matter-article {
    &__info {
      width: 100%;

      // media query props
      padding-bottom: $dimension-1-1;

      &[data-orientation="portrait"] {
        padding-bottom: $dimension-9-16;
      }
      &[data-orientation="landscape"] {
        padding-bottom: $dimension-16-9;
      }

      &-content {
        position: absolute;
        left: 0;
        right: rem(50px);
        bottom: 0; // DYNAMIC
      }
    }

    &:hover,
    &:active,
    &:focus,
    &.tapped {
      .matter-article__image,
      .matter-article__info-content {
        transform: scale(1.0125);
        box-shadow: 1px 2px 4px rgba(0, 0, 0, 0.5);
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/matter/ArticleTile.vue